import { useState } from 'react';

import { UseFormReturn } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { FormStepHeader } from './components/form-step-header';
import { DetailsForm } from './schema';
import { InfoCard } from '../../../../components/info-card';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import useUpdateMatter from '@/api/mutations/useUpdateMatter';
import { useGetFiles } from '@/api/queries/useGetFiles';
import { useGetMatterUploadHistory } from '@/api/queries/useGetMatterUploadHistory';
import { DetailsInput } from '@/new/components/details-input';
import { GenerateDetailsButton } from '@/new/components/generate-details-button';
import { Icon } from '@/new/components/icon';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/new/components/shadcn/dialog';

export function MatterCreatorDetails({
  form,
  isGeneratingDetails,
  disabled,
  onGenerateDetails,
}: {
  form: UseFormReturn<DetailsForm>;
  isGeneratingDetails?: boolean;
  disabled?: boolean;
  onGenerateDetails?: (file: File) => void;
}) {
  const { matterId } = useParams();
  const navigate = useNavigate();
  const { mutateAsync: updateMatter, isLoading } = useUpdateMatter(matterId ?? '');
  const { data: files } = useGetFiles(matterId ?? '');
  const { data: uploadHistory } = useGetMatterUploadHistory(matterId ?? '');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const fileCount = files?.length ?? 0;

  const handleCreateMatter = async () => {
    const isValid = await form.trigger();
    if (isValid) {
      try {
        if (uploadHistory?.some((u) => u.status === 'processing')) {
          toast.info('Please wait for file processing to complete before creating the matter.');
          navigate(`/matters/new/${matterId}/files`);
          return;
        }

        const values = form.getValues();
        trackCustomEvent(TrackingEventsV2.MatterEdit, { matterId });
        await updateMatter({
          issues: values.issues,
          disputeType: values.disputeType,
          userDefinedEntities: values.userDefinedEntities,
          additionalContext: values.additionalContext,
          timePeriod: values.timePeriod,
          disputeStatus: values.disputeStatus || undefined,
        });
        setIsDialogOpen(true);
      } catch (error) {
        toast.error('Failed to update matter');
      }
    }
  };

  const handleConfirmCreate = async () => {
    setIsProcessing(true);
    try {
      await updateMatter(
        { isDraft: false },
        {
          onSuccess: () => {
            navigate(`/matters/${matterId}`);
          },
          onError: () => {
            setIsProcessing(false);
            toast.error('Failed to create matter');
          },
        },
      );
    } catch {
      setIsProcessing(false);
      toast.error('Failed to create matter');
    }
  };

  if (!matterId) return null;

  return (
    <>
      <PageContainer className="flex flex-col overflow-hidden">
        <PageHeader className="flex-shrink-0">
          <FormStepHeader
            title="Matter Details"
            description="Add additional details and context to improve Wexler's understanding of your matter."
            step={3}
          >
            <Button variant="ghost" asChild>
              <Link to={`/matters/new/${matterId}/basic`}>
                <Icon className="lucide--arrow-left" />
                Back
              </Link>
            </Button>

            <Button variant="primary" onClick={handleCreateMatter} disabled={isLoading}>
              Create Matter
            </Button>
          </FormStepHeader>
        </PageHeader>

        <div className="flex flex-1 items-start justify-between overflow-y-auto pb-8">
          <div className="mx-auto mt-12 w-full max-w-3xl">
            {onGenerateDetails && (
              <div className="mb-6">
                <GenerateDetailsButton
                  isGeneratingDetails={isGeneratingDetails ?? false}
                  onGenerateDetails={onGenerateDetails}
                />
              </div>
            )}
            <DetailsInput form={form} isGeneratingDetails={isGeneratingDetails} disabled={disabled} />
          </div>
        </div>
      </PageContainer>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Matter</DialogTitle>
          </DialogHeader>

          <p>
            {fileCount === 0
              ? 'Are you sure you want to create this matter without any documents? You can always add documents later.'
              : `You have uploaded ${fileCount} document${fileCount === 1 ? '' : 's'} ready to process.`}
          </p>

          <InfoCard className="mt-5 text-xs">
            You'll be able to add other users in your organisation after you create your Matter.
          </InfoCard>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsDialogOpen(false)} disabled={isProcessing}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleConfirmCreate} disabled={isProcessing}>
              {isProcessing ? (
                <>
                  <Icon className="lucide--loader-2 animate-spin" />
                  Creating matter...
                </>
              ) : fileCount === 0 ? (
                'Create Matter'
              ) : (
                <>
                  Begin Processing {fileCount} Document{fileCount === 1 ? '' : 's'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
